<template>
  <div class="container">
    <h2 class="mb-5">Manage submissions</h2>

    <div class="row">
      <div class="col-md-2 text-left">
        <base-date
          name="month"
          v-validate="'required'"
          placeholder="From"
          v-model="collectionDateFrom"
          format="DD-MM-YYYY"
          :monthOnly="true"
        />
      </div>
      <div class="col-md-2">
        <base-date
          name="month"
          v-validate="'required'"
          placeholder="To"
          v-model="collectionDateTo"
          format="DD-MM-YYYY"
          :monthOnly="true"
        />
      </div>
      <div class="col-md-2"></div>
      <div class="col-md-6">
        <base-select
          name="company"
          label=""
          v-model="company"
          :options="userCompanies"
          col1="col-sm-0" col2="col-sm-12"
          :err="errors.collect('company', formScope)"
          select-label="name"
          @input="resetCurrentPage"
        ></base-select>
      </div>
    </div>
    <spinner :isData="!!userSubmissions.length">
      <table class="table table-bordered table-condensed tooltips">
        <thead>
          <tr>
            <th>Collection date</th>
            <th>Composition</th>
            <th>Country</th>
            <th>Net volume [{{getWeightUnit}}]</th>
            <th>Accepted date</th>
            <th style="width: 200px;">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(submission, index) in paginatedSubmissions"
            :key="index"
            v-tooltip.bottom="{html: 'tooltipComponent'}"
            @mouseenter="setCurrentSubmission(submission)"
          >
            <td>
              {{ [submission.collection_date, 'YYYY-MM-DD'] | moment('M / YYYY') }}
            </td>
            <td>
              {{compositionObj[submission.composition_id]}}
            </td>
            <td>
              {{ submission.country_code }}
            </td>
            <td>
              {{ volumeByWeightUnit(submission.volume_net) }}
            </td>
            <td>
              {{ getAcceptedDate(submission) }}
            </td>
            <td>
              <span
                v-if="!canPerformAction(submission)"
                title="Preview submission"
                class="action-icon"
                @click="preview(submission)"
              >
                <i class="fas fa-eye"></i>
              </span>
              <span
                class="action-icon"
                title="Edit submission"
                :class="{disabled: !canPerformAction(submission)}"
                @click="edit(submission)"
              >
                <i class="fas fa-pen"></i>
              </span>
              <span
                class="action-icon"
                title="Approve submission"
                v-if="canPerformApprove(submission)"
                @click="approve(submission)"
              >
                <i class="far fa-thumbs-up"></i>
              </span>
              <span
                class="action-icon"
                title="Delete submission"
                :class="{disabled: !canPerformAction(submission)}"
                @click="remove(submission)"
              >
                <i class="fas fa-ban"></i>
              </span>
              <span
                class="action-icon"
                :class="{ 'text-success': submission.isAssigned, 'text-danger': !submission.isAssigned }"
                title="Assign End Applications"
                @click="gotToApplications()"
              >
                Assign End Applications
              </span>
            </td>
          </tr>
          <tr v-if="!filteredSubmissions.length">
            <td colspan="6">No submissions.</td>
          </tr>
        </tbody>
      </table>
      <div class="row">
        <div class="col-4">
          <base-select
            label="Items"
            select-label="label"
            v-model="itemsPerPage"
            :options="itemsPerPageOptions"
          />
        </div>
        <div class="col-8 text-right">
          <r-pagination
            :totalPages="totalPages"
            :total="total"
            :currentPage="currentSubmissionsPage"
            @pageChanged="handlePageChanged"
          />
        </div>
     </div>
    </spinner>
    <!-- <div id="tooltipComponent"> -->
    <div id="tooltipComponent">
      <table class="table table-bordered table-condensed mb-0">
        <tbody>
          <tr>
            <td style="width: 180px;">Company:</td>
            <td>{{currentSubmission ? getCompany(currentSubmission) : ''}}</td>
          </tr>
          <tr>
            <td>Collection date:</td>
            <td>{{currentSubmission ? currentSubmission.collection_date.substr(0, 7) : ''}}</td>
          </tr>
          <tr>
            <td>Gross volume:</td>
            <td>{{ currentSubmission ? currentSubmission.volume_gross : '' }}</td>
          </tr>
          <tr>
            <td>Net volume:</td>
            <td>{{ currentSubmission ? currentSubmission.volume_net : '' }}</td>
          </tr>
          <tr>
            <td>Comments:</td>
            <td>{{ currentSubmission ? currentSubmission.comments : '' }}</td>
          </tr>
          <tr>
            <td>Created at:</td>
            <td>{{ currentSubmission ? currentSubmission.createdAt.substr(0, 10) : '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row">
      <div class="col-md-6">
        <base-button radius="bl" component-class="blue" @click.native="$router.go(-1)">
          <i class="fas fa-arrow-left mr-3"></i> Back
        </base-button>
      </div>
    </div>

  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import * as moment from 'moment';
  import _sum from 'lodash/sum';

  import Pagination from '@/components/Pagination/Pagination';
  import BaseButton from '@/components/inputs/button/BaseButton';
  import BaseSelect from '@/components/inputs/select/BaseSelect';
  import BaseDate from '@/components/inputs/text/BaseDate';
  import Spinner from '@/components/spinner/Spinner';
  import constances from '@/configs/constances';

  export default {
    data() {
      return {
        itemsPerPage: 5,
        itemsPerPageOptions: [5, 15, 30, 50],
        year: moment().format('YYYY'),
        formScope: 'manageApplications',
        company: { id: null },
        currentSubmission: null,
        compositionApplicationSum: {},
        collectionDateFrom: null,
        collectionDateTo: null,
      };
    },

    computed: {
      ...mapState({
        userCompositions: state => state.userCompositions.data,
        userCompanies: state => state.userCompanies.loggedUserCompanies,
        // userCompanies: state => state.userCompanies.data,
        userApplicationsYears: state => state.userApplications.years,
        userGroupedApplicationLinkValues: state => state.userApplications.groupedApplicationLinkValues,
        userSubmissions: state => state.userSubmissions.data,
        currentSubmissionsPage: state => state.userSubmissions.currentSubmissionsPage,
      }),
      isRecycler() {
        return this.$store.getters['user/isRecycler'];
      },
      getWeightUnit() {
        return this.$store.getters['user/getWeightUnit'];
      },
      computedCollectionDateFrom() {
        this.resetCurrentPage();
        const [month, year] = this.collectionDateFrom ? this.collectionDateFrom.split('-') : ['', ''];
        return month && year ? `${year}-${month.length === 2 ? month : '0' + month}-01` : false;
      },
      computedCollectionDateTo() {
        this.resetCurrentPage();
        const [month, year] = this.collectionDateTo ? this.collectionDateTo.split('-') : [null, null];
        const days = month ? this.getMontDaysLength(`${year}-${month.length === 2 ? month : '0' + month}`) : '01';
        return month && year ? `${year}-${month.length === 2 ? month : '0' + month}-${days}` : false;
      },
      filteredSubmissions () {
        const selectedYearCompanySubmission = this.userSubmissions
          .filter((submission) => {
            const isDateFrom = this.computedCollectionDateFrom ? moment(submission.collection_date).isSameOrAfter(this.computedCollectionDateFrom) : true;
            const isDateTo = this.computedCollectionDateTo ? moment(submission.collection_date).isSameOrBefore(this.computedCollectionDateTo) : true;
            const selectedComapny = submission.company_id === this.company.id;
            return isDateFrom && isDateTo && selectedComapny;
          }).map(submission => {
          submission.isAssigned = this.isCompositionAssigned(submission);
          return submission;
        });

        return selectedYearCompanySubmission;
      },
      paginatedSubmissions() {
        const skipUsersSubmissions =
          this.currentSubmissionsPage === 1 ? 0 : (this.currentSubmissionsPage - 1) * this.itemsPerPage;
        return this.filteredSubmissions.slice(skipUsersSubmissions, skipUsersSubmissions + this.itemsPerPage);
      },
      compositionObj() {
        const obj = {};
        this.userCompositions.forEach(composition => obj[composition.id] = composition.human_name || composition.name);
        return obj;
      },
      total() {
        return this.filteredSubmissions.length;
      },
      totalPages() {
          return Math.ceil(this.total / this.itemsPerPage);
      },
    },

    watch: {
      userCompanies(nextValue) {
        this.company = nextValue[0];
      },
      itemsPerPage() {
          this.resetCurrentPage();
      },
      userGroupedApplicationLinkValues(newValue) {
        Object.keys(newValue)
          .forEach(key => this.compositionApplicationSum[key] = _sum(newValue[key]));
      }
    },

    methods: {
      ...mapActions({
        // getUserCompanies: 'userCompanies/getCompanies',
        getUserAttachedCompany: 'userCompanies/getUserAttachedCompany',
        getUserSubmissions: 'userSubmissions/getSubmissions',
        getUserCompositions: 'userCompositions/getCompositions',
        removeUserSubmission: 'userSubmissions/remove',
        approveUserSubmission: 'userSubmissions/approve',
        getApplicationLinks: 'userApplications/getApplicationLinks',
      }),
      getMontDaysLength(date) {
        return moment(date, 'YYYY-MM').daysInMonth();
      },
      resetCurrentPage() {
        this.$store.commit('userSubmissions/setCurrentSubmissionsPage', 1);
      },
      handlePageChanged(page) {
        this.$store.commit('userSubmissions/setCurrentSubmissionsPage', page);
      },
      volumeByWeightUnit(volume) {
        if (this.getWeightUnit === 'ton') {
          return volume / 1000;
        }
        return volume;
      },
      getAcceptedDate(submission) {
        if (!submission.accepted_date) {
          return '-- not accepted --';
        }
        return moment(submission.accepted_date).format('YYYY-MM-DD');
      },
      preview (submission) {
        this.$router.push({name: 'user.show-submission', params: {id: submission.id}});
      },
      showModal(action) {
        this.$modal.show('dialog', {
          title: 'Action disabled.',
          text: `${action} action is disabled because this submission was added more than ${constances.submissionCanEditByDays} days ago.`,
          buttons: [
            {
              title: 'Close'
            }
          ]
        });
      },
      edit(submission) {
        if (this.canPerformAction(submission)) {
          this.$router.push({name: 'user.show-submission', params: {id: submission.id}});
        }
        else {
          this.showModal('Edit');
        }
      },
      remove(submission) {
        if (this.canPerformAction(submission)) {
          this.removeUserSubmission(submission.id);
        }
        else {
          this.showModal('Delete');
        }
      },
      canPerformAction(submission) {
        const days = moment().diff(moment(submission.createdAt), 'days');
        return days <= constances.submissionCanEditByDays;
      },
      canPerformApprove(submission) {
        if (this.isRecycler) {
          return false;
        }
        return submission.accepted_date === null;
      },
      approve(submission) {
        return this.approveUserSubmission(submission.id);
      },
      getCompany(submission) {
        if (this.userCompanies) {
          return this.userCompanies.filter(e => e.id === submission.company_id)[0].name;
        }
        return null;
      },
      setCurrentSubmission(submission) {
        this.currentSubmission = submission;
      },
      gotToApplications() {
        const { company } = this;
        this.$router.push({ name: 'user.applications', params: { company } });
      },
      isCompositionAssigned(submission) {
        const { composition_id, volume_net } = submission;
        const currentCompositionApplicationSum = this.compositionApplicationSum[composition_id];

        if (volume_net <= currentCompositionApplicationSum) {
          this.compositionApplicationSum[composition_id] = currentCompositionApplicationSum - volume_net;
          return true;
        }

        return false;
      }
    },

    mounted () {
      // this.getUserCompanies();
      // this.getUserCompanies(this.$store.getters['user/getLoggedInUserId']);
      this.getUserAttachedCompany(this.$store.getters['user/getLoggedInUserId']);
      this.getUserCompositions();
      this.getUserSubmissions();
      this.getApplicationLinks();
    },

    components: {
      'base-button': BaseButton,
      'spinner': Spinner,
      'base-select': BaseSelect,
      'r-pagination': Pagination,
      'base-date': BaseDate,
    }
  };

</script>
